import React, { useState, useEffect, useCallback } from 'react'
import { useMutation } from '@apollo/client'
import { Alert, Box, Typography, IconButton, Chip, Avatar } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { getLanguageChipClass } from 'services/languages'
import { formatContentTypeLabel } from 'gui/utils/content-type-label'
import ContentTypeChip from 'gui/components/common/ContentTypeChip'
import 'gui/components/common/_shared-styles.scss'
import 'gui/components/common/ContentAutocomplete.scss'
import {
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_PROMOTION,
  CONTENT_TYPE_COURSE,
  CONTENT_TYPE_TAG,
} from 'services/content/content-types'
import ContentAutocomplete from 'gui/components/common/ContentAutocomplete'
import { CREATE_CONTENT_REFERENCES } from './content-references/queries'
import type { SectionManagedSectionProps, ContentReference } from './types'

const TAGGABLE_CONTENT_TYPES = [
  CONTENT_TYPE_VIDEO,
  CONTENT_TYPE_SERIES,
  CONTENT_TYPE_PROMOTION,
  CONTENT_TYPE_COURSE,
]

/** Match ContentAutocomplete dropdown row thumbnail */
const QUEUE_THUMB_WIDTH = 89
const QUEUE_THUMB_HEIGHT = 50

export interface TagNewContentSectionProps extends SectionManagedSectionProps {
  /** Numeric Brooklyn content id of this tag (to side of the reference). */
  tagContentId?: number | null
  /** Content reference type — tag's filterType field. */
  tagFilterType?: string | null
  /** Fallback reference type — tag's bundle field. */
  tagBundle?: string | null
}

/**
 * Section-managed block: queue content to receive this tag, save creates references
 * from each queued item (from) to this tag (to) with type = filterType || bundle.
 */
function TagNewContentSection ({
  tagContentId,
  tagFilterType,
  tagBundle,
  isViewOnly,
  onDirtyChange,
  saveHandlerRef,
  onResetTriggered = 0,
}: TagNewContentSectionProps) {
  const [selected, setSelected] = useState<ContentReference[]>([])
  const [autocompleteValue, setAutocompleteValue] = useState<ContentReference[]>([])

  const [createContentReferences] = useMutation(CREATE_CONTENT_REFERENCES)

  const referenceType = (tagFilterType?.trim() || tagBundle?.trim()) ?? ''
  const hasReferenceType = referenceType.length > 0

  useEffect(() => {
    onDirtyChange(selected.length > 0)
  }, [selected, onDirtyChange])

  useEffect(() => {
    if (onResetTriggered > 0) {
      setSelected([])
      setAutocompleteValue([])
    }
  }, [onResetTriggered])

  const handleSelect = useCallback((item: ContentReference | null) => {
    if (!item) return
    setSelected((prev) => {
      if (prev.some((p) => p.contentId === item.contentId)) return prev
      return [...prev, item]
    })
    setAutocompleteValue([])
  }, [])

  const handleRemove = useCallback((contentId: number) => {
    setSelected((prev) => prev.filter((p) => p.contentId !== contentId))
  }, [])

  const handleSave = useCallback(async () => {
    if (selected.length === 0) return
    const toId = tagContentId
    if (toId == null || !referenceType) return

    const contentReferences = selected.map((item) => ({
      fromContentContentId: item.contentId,
      fromContentContentType: item.contentType,
      toContentContentId: toId,
      toContentContentType: CONTENT_TYPE_TAG,
      type: referenceType,
    }))

    await createContentReferences({
      variables: {
        contentReferences,
        deleteOmittedReferences: false,
      },
    })

    setSelected([])
    setAutocompleteValue([])
  }, [selected, tagContentId, referenceType, createContentReferences])

  useEffect(() => {
    const ref = saveHandlerRef
    ref.current = handleSave
    return () => {
      ref.current = null
    }
  }, [saveHandlerRef, handleSave])

  if (!hasReferenceType) {
    return (
      <Alert severity="info" sx={{ m: 1 }}>
        Set a Filter Type or Bundle in the section above to enable tagging content.
      </Alert>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      {selected.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {selected.map((item) => (
            <Box
              key={item.contentId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: item.published ? '#4caf50' : '#f44336',
                  flexShrink: 0,
                }}
              />
              <Avatar
                src={item.thumbnailUrl || undefined}
                variant="square"
                sx={{
                  width: QUEUE_THUMB_WIDTH,
                  height: QUEUE_THUMB_HEIGHT,
                  borderRadius: 1,
                  bgcolor: item.thumbnailUrl ? 'transparent' : 'grey.100',
                }}
              >
                {!item.thumbnailUrl && (
                  <Box sx={{ fontSize: '10px', color: 'text.disabled', px: 0.5, textAlign: 'center' }}>
                    No Image
                  </Box>
                )}
              </Avatar>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, flexWrap: 'wrap' }}>
                <ContentTypeChip contentType={item.contentType} label={formatContentTypeLabel(item)} />
                {item.language && (
                  <Chip
                    label={item.language}
                    size="small"
                    variant="outlined"
                    className={`language-chip ${getLanguageChipClass(item.language)} tag-new-content-queue__language-chip`}
                  />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" component="span" display="block">
                  {item.title || '(untitled)'}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  ID: {item.contentId}
                </Typography>
              </Box>
              {!isViewOnly && (
                <IconButton
                  size="small"
                  aria-label={`Remove ${item.title}`}
                  onClick={() => handleRemove(item.contentId)}
                  sx={{ flexShrink: 0 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      <ContentAutocomplete
        value={autocompleteValue}
        onChange={setAutocompleteValue}
        onSelect={handleSelect}
        label="Search content by Title"
        disabled={isViewOnly}
        contentTypes={TAGGABLE_CONTENT_TYPES}
      />
    </Box>
  )
}

export default TagNewContentSection
